import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

import { useFormatCurrency } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import { AccordionItem } from "@krea/customer-features/shared-components/accordion/AccordionItem";
import Button from "@krea/customer-features/shared-components/button";
import { ResultContent } from "@krea/customer-features/shared-components/preliminary-bid-result/ResultContent";
import { Text } from "@krea/customer-features/shared-components/text";
import { useLoanApplicationFormStore } from "@krea/customer-features/store/loan-application-form/hooks";
import {
  googleCalendarUrlSE,
  googleCalendarUrlFI,
} from "@krea/shared/constants/index";
import { CountryCode } from "@krea/shared/types/common";

const CALENDAR_URL_MAP: Record<CountryCode, string> = {
  [CountryCode.SE]: `${googleCalendarUrlSE}##follow`,
  [CountryCode.FI]: `${googleCalendarUrlFI}##follow`,
};

const PreliminaryBidResult = ({
  isOnResultPage = false,
}: {
  isOnResultPage?: boolean;
}) => {
  const { t } = useTranslation();
  const [formattedCurrencyValue] = useFormatCurrency({
    currency: settings.currency,
  });

  const navigate = useNavigate();

  const goBack = () => {
    navigate("/amount-estimator");
  };

  const { organisationName, preliminaryBid } = useLoanApplicationFormStore(
    useShallow((state) => ({
      organisationName: state.organisation.name,
      preliminaryBid: state.preliminaryBid,
    })),
  );

  const probabilityOptions = {
    low: t("preliminaryBidForm.probabilityOptions.low"),
    medium: t("preliminaryBidForm.probabilityOptions.medium"),
    high: t("preliminaryBidForm.probabilityOptions.high"),
  };

  const estimatedLowerAmount = preliminaryBid?.estimatedMinLoanAmount;

  const estimatedAmount = preliminaryBid?.estimatedMaxLoanAmount;

  const isEdgeCase = preliminaryBid?.useEdgeCaseLogic;

  const maxAccuracyAmount =
    settings.countryCode === CountryCode.SE ? 3000000 : 300000;

  const probabilityKey =
    preliminaryBid &&
    (preliminaryBid.probabilityLoanApproved as keyof typeof probabilityOptions);

  const probability = probabilityKey
    ? probabilityOptions[probabilityKey]
    : null;

  const reasonForLowProbabilityTextMap: Record<CountryCode, string> = {
    [CountryCode.SE]: t(
      "preliminaryBidForm.reasonForLowProbabilityDescription2Swedish",
    ),
    [CountryCode.FI]: t(
      "preliminaryBidForm.reasonForLowProbabilityDescription2Finnish",
    ),
  };

  if (isEmpty(preliminaryBid)) {
    return null;
  }
  if (isOnResultPage)
    return (
      <ResultContent
        headingContent={
          <>
            <Text size="sm" className="tw-font-normal">
              {organisationName}
            </Text>

            {isEdgeCase ? (
              <Text size="lg" className="tw-font-bold">
                {t("preliminaryBidForm.weCouldNotEstimateYourCreditLimit")}
              </Text>
            ) : (
              <>
                {(probability === probabilityOptions.high ||
                  probability === probabilityOptions.medium) && (
                  <Text size="lg" className="tw-font-bold">
                    {estimatedAmount &&
                      estimatedLowerAmount !== undefined &&
                      formattedCurrencyValue(estimatedLowerAmount)}
                    {" - "}
                    {estimatedAmount && formattedCurrencyValue(estimatedAmount)}
                  </Text>
                )}
                {probability === probabilityOptions.low && (
                  <Text size="lg" className="tw-font-bold">
                    {t("preliminaryBidForm.lowLikelihood")}
                  </Text>
                )}
              </>
            )}
          </>
        }
        backButton={{
          onBack: goBack,
          buttonText: t("preliminaryBidForm.makeAnotherEstimate"),
        }}
        probability={probability}
        probabilityOptions={probabilityOptions}
        isEdgeCase={isEdgeCase}
      >
        <>
          {isEdgeCase ? (
            <>
              <div className="tw-mb-5 tw-rounded tw-border tw-border-solid tw-px-5 tw-py-4">
                <Text size="md" className="tw-font-normal">
                  {t("preliminaryBidForm.ourEstimateIsAccurateUpTo")}{" "}
                  {formattedCurrencyValue(maxAccuracyAmount)}
                </Text>
              </div>

              <div className="2xl:tw-flex tw-mb-5 tw-items-center tw-rounded tw-border tw-border-solid tw-px-5 tw-py-4 ">
                <Text size="md" className="tw-font-normal">
                  {t("preliminaryBidForm.doYouWantAnEstimateFromAnAdvisor")}
                </Text>

                <a
                  href={CALENDAR_URL_MAP[settings.countryCode]}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="text" size="sm" className="tw-font-bold">
                    {t("preliminaryBidForm.bookAMeeting")}
                  </Button>
                </a>
              </div>
            </>
          ) : (
            <>
              {probability === probabilityOptions.high && (
                <div className="tw-pb-4">
                  <div className="tw-mb-5 tw-rounded tw-border tw-border-solid tw-px-5 tw-py-4">
                    <Text size="md" className="tw-font-normal">
                      {t("preliminaryBidForm.largerAmountsPossible")}
                    </Text>
                  </div>

                  <AccordionItem
                    titleStyling="tw-font-normal tw-text-md"
                    title={t("preliminaryBidForm.seeHowWeEstimated")}
                  >
                    <Text size="sm">
                      {t("preliminaryBidForm.howWeEstimatedDescription1")}{" "}
                      <br />
                      <br />
                      {t("preliminaryBidForm.howWeEstimatedDescription2")}
                      <br />
                      <br />
                      {t("preliminaryBidForm.howWeEstimatedDescription3")}
                    </Text>
                  </AccordionItem>
                </div>
              )}
              {probability === probabilityOptions.medium && (
                <div className="tw-pb-4">
                  <AccordionItem
                    titleStyling="tw-font-normal tw-text-md"
                    className="tw-mb-5"
                    title={t("preliminaryBidForm.howCanYouImproveYourChances")}
                  >
                    <Text size="sm">
                      {t("preliminaryBidForm.improveYourChancesDescription1")}
                      <br />
                      <br />
                      {t("preliminaryBidForm.improveYourChancesDescription2")}
                      <br />
                      <br />
                      <ul>
                        <li>
                          {t(
                            "preliminaryBidForm.improveYourChancesDescription3",
                          )}
                        </li>
                        <li>
                          {t(
                            "preliminaryBidForm.improveYourChancesDescription4",
                          )}
                        </li>
                        <li>
                          {t(
                            "preliminaryBidForm.improveYourChancesDescription5",
                          )}
                        </li>
                      </ul>
                      {t("preliminaryBidForm.improveYourChancesDescription6")}
                      <br />
                      <br />
                      {t("preliminaryBidForm.improveYourChancesDescription7")}
                    </Text>
                  </AccordionItem>

                  <AccordionItem
                    titleStyling="tw-font-normal tw-text-md"
                    title={t("preliminaryBidForm.seeHowWeEstimated")}
                  >
                    <Text size="sm">
                      {t("preliminaryBidForm.howWeEstimatedDescription1")}{" "}
                      <br />
                      <br />
                      {t("preliminaryBidForm.howWeEstimatedDescription2")}
                      <br />
                      <br />
                      {t("preliminaryBidForm.howWeEstimatedDescription3")}
                    </Text>
                  </AccordionItem>
                </div>
              )}
              {probability === probabilityOptions.low && (
                <div>
                  <AccordionItem
                    titleStyling="tw-font-normal tw-text-md"
                    className="tw-mb-4"
                    title={t("preliminaryBidForm.why")}
                  >
                    <Text size="sm">
                      {t(
                        "preliminaryBidForm.reasonForLowProbabilityDescription1",
                      )}
                      <br /> <br />
                      <ul>
                        <li>
                          {reasonForLowProbabilityTextMap[settings.countryCode]}
                        </li>
                        <li>
                          {t(
                            "preliminaryBidForm.reasonForLowProbabilityDescription3",
                          )}
                        </li>
                        <li>
                          {t(
                            "preliminaryBidForm.reasonForLowProbabilityDescription4",
                          )}
                        </li>
                      </ul>
                      {t(
                        "preliminaryBidForm.reasonForLowProbabilityDescription5",
                      )}
                      <br /> <br />
                      {t(
                        "preliminaryBidForm.reasonForLowProbabilityDescription6",
                      )}
                    </Text>
                  </AccordionItem>
                </div>
              )}
            </>
          )}
        </>
      </ResultContent>
    );

  if (!isEdgeCase) {
    return (
      <div className="tw-w-full tw-bg-gray tw-px-7 tw-py-3">
        <p className="tw-mb-0">
          {t("preliminaryBidForm.weEstimateThatCompanyCanReceive", {
            organisationName,
          })}
          <b>
            {estimatedAmount !== undefined
              ? formattedCurrencyValue(estimatedAmount)
              : ""}
          </b>{" "}
          &nbsp;
        </p>
      </div>
    );
  }

  return null;
};

export default PreliminaryBidResult;
